import { type MaintenanceChecklistSection } from '@gb-ticket/shared-types';
import { useEffect, useRef, useState } from 'react';
import type * as pdfjs from 'pdfjs-dist';

import { loadMaintenancePdfDocument } from '@/products/maintenance/features/pdf/load-maintenance-pdf-document';
import { ProtocolAcroformFields } from '@/products/maintenance/features/protocol/protocol-acroform-fields';

type Props = {
  pdfUrl: string;
  section: MaintenanceChecklistSection;
  values: Record<string, unknown>;
  disabled?: boolean;
  onValueChange: (fieldKey: string, value: unknown) => void;
};

export function ProtocolAcroformSheet({ pdfUrl, section, values, disabled, onValueChange }: Props) {
  const [pdf, setPdf] = useState<pdfjs.PDFDocumentProxy | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const placed = section.fields.filter((field) => field.placement);
  const pageCount =
    pdf?.numPages ?? Math.max(1, ...placed.map((field) => field.placement?.page ?? 1));

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const document = await loadMaintenancePdfDocument(pdfUrl);
        if (!cancelled) {
          setPdf(document);
          setLoadError(null);
        } else {
          void document.cleanup();
        }
      } catch (error: unknown) {
        if (!cancelled) {
          setPdf(null);
          setLoadError(
            error instanceof Error ? error.message : 'Die PDF konnte nicht geladen werden.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  return (
    <div className="w-full space-y-4">
      <h2 className="text-lg font-semibold">{section.title}</h2>
      {Array.from({ length: pageCount }, (_, index) => {
        const pageNumber = index + 1;
        const fields = placed.filter((field) => field.placement?.page === pageNumber);
        return (
          <AcroformPage
            key={pageNumber}
            pdf={pdf}
            pageNumber={pageNumber}
            loadError={loadError}
            fields={fields}
            values={values}
            disabled={disabled}
            onValueChange={onValueChange}
          />
        );
      })}
    </div>
  );
}

function AcroformPage({
  pdf,
  pageNumber,
  loadError,
  fields,
  values,
  disabled,
  onValueChange,
}: {
  pdf: pdfjs.PDFDocumentProxy | null;
  pageNumber: number;
  loadError: string | null;
  fields: MaintenanceChecklistSection['fields'];
  values: Record<string, unknown>;
  disabled?: boolean;
  onValueChange: (fieldKey: string, value: unknown) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [pageAspect, setPageAspect] = useState<number | null>(null);

  useEffect(() => {
    if (!pdf) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const page = await pdf.getPage(pageNumber);
      const layoutViewport = page.getViewport({ scale: 1 });
      if (!cancelled) {
        setPageAspect(layoutViewport.width / layoutViewport.height);
      }
      const viewport = page.getViewport({ scale: 2 });
      const canvas = canvasRef.current;
      if (!canvas || cancelled) {
        return;
      }
      const context = canvas.getContext('2d');
      if (!context) {
        return;
      }
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: context, viewport, canvas }).promise;
    })().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [pageNumber, pdf]);

  return (
    <div className="w-full space-y-2">
      {loadError && pageNumber === 1 ? (
        <p className="text-sm text-destructive">{loadError}</p>
      ) : null}
      <div className="overflow-x-auto rounded-md border bg-muted">
        <div
          className="relative w-full min-w-0"
          style={
            pdf && pageAspect ? { aspectRatio: pageAspect } : { minHeight: 'min(75dvh, 70rem)' }
          }
        >
          <canvas ref={canvasRef} className="block h-full w-full" />
          {pdf ? (
            <div className="absolute inset-0">
              <ProtocolAcroformFields
                fields={fields}
                values={values}
                disabled={disabled}
                onValueChange={onValueChange}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
